import React from "react";

/* ══════════════════════════════════════════════════════════════
   PROCUREX LOGO — AI-Powered Procurement, Supply Chain & Contracts
   Spec: user-provided samgraha-logo-{light,dark}.svg
   Concept: "River Delta" — a single source splits into 3 branching
   paths with an orange AI spark at the source point.
   Special: "g" in ProcureX is orange italic.
   Theme-aware: wordmark / Devanagari / descriptor swap + spark
   inner dot matches theme bg.
   ══════════════════════════════════════════════════════════════ */

/* River Delta icon — standalone. Paths verbatim from source (translate removed). */
export function ProcureXIcon({ size = 56, className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 70"
      width={size}
      height={Math.round(size * 70 / 64)}
      aria-hidden="true"
      className={`sg-mark ${className}`}
    >
      {/* Trunk */}
      <path d="M30 4 L30 20" stroke="#1E4E8C" strokeWidth="4.5" strokeLinecap="round" fill="none" />
      {/* 3 branches */}
      <path d="M30 20 Q28 30, 12 44" stroke="#1E4E8C" strokeWidth="2.8" strokeLinecap="round" fill="none" />
      <path d="M30 20 Q30 32, 30 46" stroke="#2E70AD" strokeWidth="2.2" strokeLinecap="round" fill="none" />
      <path d="M30 20 Q32 30, 48 44" stroke="#5790C9" strokeWidth="1.8" strokeLinecap="round" fill="none" />
      {/* Sub-branches (theme opacity via CSS) */}
      <path d="M12 44 Q6 52, 4 62" stroke="#1E4E8C" strokeWidth="2" strokeLinecap="round" fill="none" className="sg-sub-a" />
      <path d="M12 44 Q16 52, 18 62" stroke="#1E4E8C" strokeWidth="1.4" strokeLinecap="round" fill="none" className="sg-sub-b" />
      <path d="M30 46 Q26 54, 24 62" stroke="#2E70AD" strokeWidth="1.4" strokeLinecap="round" fill="none" className="sg-sub-b" />
      <path d="M30 46 Q34 54, 36 62" stroke="#2E70AD" strokeWidth="1.4" strokeLinecap="round" fill="none" className="sg-sub-b" />
      <path d="M48 44 Q52 52, 56 62" stroke="#5790C9" strokeWidth="1.2" strokeLinecap="round" fill="none" className="sg-sub-b" />
      <path d="M48 44 Q44 52, 42 62" stroke="#5790C9" strokeWidth="1" strokeLinecap="round" fill="none" className="sg-sub-c" />
      {/* Orange spark at source — inner dot matches theme bg */}
      <circle cx="30" cy="18" r="5" fill="#F26B1F" />
      <circle cx="30" cy="18" r="2" className="sg-spark-core" />
    </svg>
  );
}

/* Full horizontal logo — matches family pattern (icon left + wordmark/Devanagari/descriptor right). */
export default function ProcureXLogo({ height = 100, className = "" }) {
  const viewW = 620;
  const viewH = 100;
  const w = Math.round(viewW * height / viewH);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${viewW} ${viewH}`}
      width={w}
      height={height}
      role="img"
      aria-label="ProcureX — AI-Powered Procurement, Supply Chain & Contracts Lifecycle Management"
      className={`sg-logo-full ${className}`}
    >
      {/* RIVER DELTA ICON */}
      <g transform="translate(20, 10)">
        <path d="M30 4 L30 20" stroke="#1E4E8C" strokeWidth="4.5" strokeLinecap="round" fill="none" />
        <path d="M30 20 Q28 30, 12 44" stroke="#1E4E8C" strokeWidth="2.8" strokeLinecap="round" fill="none" />
        <path d="M30 20 Q30 32, 30 46" stroke="#2E70AD" strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <path d="M30 20 Q32 30, 48 44" stroke="#5790C9" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        <path d="M12 44 Q6 52, 4 62" stroke="#1E4E8C" strokeWidth="2" strokeLinecap="round" fill="none" className="sg-sub-a" />
        <path d="M12 44 Q16 52, 18 62" stroke="#1E4E8C" strokeWidth="1.4" strokeLinecap="round" fill="none" className="sg-sub-b" />
        <path d="M30 46 Q26 54, 24 62" stroke="#2E70AD" strokeWidth="1.4" strokeLinecap="round" fill="none" className="sg-sub-b" />
        <path d="M30 46 Q34 54, 36 62" stroke="#2E70AD" strokeWidth="1.4" strokeLinecap="round" fill="none" className="sg-sub-b" />
        <path d="M48 44 Q52 52, 56 62" stroke="#5790C9" strokeWidth="1.2" strokeLinecap="round" fill="none" className="sg-sub-b" />
        <path d="M48 44 Q44 52, 42 62" stroke="#5790C9" strokeWidth="1" strokeLinecap="round" fill="none" className="sg-sub-c" />
        <circle cx="30" cy="18" r="5" fill="#F26B1F" />
        <circle cx="30" cy="18" r="2" className="sg-spark-core" />
      </g>

      {/* WORDMARK — "Pro" + orange italic "c" + "ureX" — family style (italic Playfair Display) */}
      <text y="76" fontFamily="'Playfair Display', Georgia, serif" fontSize="82" fontWeight="400" fontStyle="italic" letterSpacing="-0.02em">
        <tspan x="96" className="sg-wordmark-text">Pro</tspan>
        <tspan fill="#F26B1F" fontStyle="italic" fontWeight="400">c</tspan>
        <tspan className="sg-wordmark-text">ureX</tspan>
      </text>
    </svg>
  );
}
